import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface DonutSegment { id: string; label: string; value: number; color: string; }
export interface DonutArc {
  id: string; label: string; color: string;
  dash: number; offset: number; pct: number;
}

/** Radius inside the fixed 100x100 viewBox. r + thickness/2 must stay <= 50. */
export const DONUT_R = 42;
export const DONUT_C = 2 * Math.PI * DONUT_R;

/**
 * Values to stroke-dash geometry. Pure.
 *
 * Non-positive values are dropped rather than drawn: a negative dash renders
 * backwards over its neighbours, and an all-zero month must show the empty
 * track instead of dividing by zero.
 */
export function donutArcs(segments: DonutSegment[]): DonutArc[] {
  const usable = (segments ?? []).filter(s => s.value > 0);
  const total = usable.reduce((n, s) => n + s.value, 0);
  if (total <= 0) return [];

  let cumulative = 0;
  return usable.map(s => {
    const dash = (s.value / total) * DONUT_C;
    const arc: DonutArc = {
      id: s.id, label: s.label, color: s.color,
      dash, offset: -cumulative, pct: (s.value / total) * 100,
    };
    cumulative += dash;
    return arc;
  });
}

@Component({
  selector: 'mm-donut',
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss'],
  standalone: false,
})
export class DonutChartComponent {
  @Input() segments: DonutSegment[] = [];
  /** Pre-formatted — charts never format money. */
  @Input() centerValue = '';
  @Input() centerLabel = '';
  @Input() thickness = 14;
  @Input() selectedId: string | null = null;
  @Input() ariaLabel = 'Spending by group';

  @Output() select = new EventEmitter<string>();

  readonly r = DONUT_R;
  readonly c = DONUT_C;

  get arcs(): DonutArc[] { return donutArcs(this.segments); }
  get isEmpty(): boolean { return this.arcs.length === 0; }

  trackArc(_: number, arc: DonutArc): string { return arc.id; }

  // preventDefault: Space on a focused element scrolls the page as well as
  // activating it, which is jarring when the chart is mid-viewport.
  onSelect(arc: DonutArc, event?: Event): void {
    event?.preventDefault();
    this.select.emit(arc.id);
  }
}
